import PatPoints from "../../football/enums/pat-points";
import FirstDownStyles from "../../football/enums/first-down-styles";
import BooleanType from "../../football/enums/boolean-type";
import ClockType from "../../football/enums/clock-type";
import FieldLength from "../../football/enums/field-length";
import FieldAreaType from "../../football/enums/field-area-type";
import TeamLoss from "../../football/enums/team-loss";
import ParticipationStyle from "../../football/enums/participation-style";

const customFormData = {
    // Section render order. Four of these are config sections and could ride
    // on config key order like they always have; "fieldAreas" is not - it
    // groups settings that stay at general.* in storage, so nothing about the
    // config can position it. Declaring all five keeps the order explicit
    // rather than accidental.
    sectionOrder: ["general", "fieldAreas", "clock", "spots", "scoring"],
    general: {
        title: "Game information",
        order: 1,
        downs: {
            order: 7,
            description:
                "Default number of downs before the ball is turned over automatically, barring a first down."
        },
        endZoneLength: {
            title: "End Zone Length (yds)",
            section: "fieldAreas",
            row: 1,
            order: 4,
            description:
                "Length of the end zone, north to south, used to calculate yards earned in the end zone."
        },
        fieldLength: {
            title: "Field Length (yds)",
            options: Object.values(FieldLength).filter(
                ({ code }) =>
                    code % 4 === 0 || code === FieldLength.OptionTwo.code
            ),
            section: "fieldAreas",
            row: 1,
            order: 2,
            description:
                "Distance in yards north and south on a given field of play, not including the end zone."
        },
        fieldWidth: {
            title: "Field Width (yds)",
            section: "fieldAreas",
            row: 1,
            order: 3,
            description: "Distance in yards east to west."
        },
        firstDownFlagStyle: {
            title: "First Down Setting",
            options: Object.values(FirstDownStyles),
            component: "radioGroup",
            section: "fieldAreas",
            // Opens the section. The three yardage fields complete its row and
            // the control it drives comes after them: the "First Down Every
            // (yds)" input packs onto the end of the row when there is room
            // (landing under this radio), while the First Down Spots ruler is
            // full width and always takes its own line. One form-data row for
            // all of them, so the UI's grid packs each width into rows whose
            // counts never grow downwards.
            row: 1,
            order: 1,
            description:
                "Determines whether a first down is earned by gaining yards to a marker or by reaching the next zone."
        },
        firstDownYards: {
            title: "First Down Every (yds)",
            min: 1,
            // Shares its cell with First Down Spots: the First Down Setting
            // decides which of the two is on screen (visibleWhen). Each is a
            // field of its own, so the form marks and validates the value it
            // actually shows.
            visibleWhen: {
                key: "general.firstDownFlagStyle",
                values: [FirstDownStyles.YardToGain.code]
            },
            section: "fieldAreas",
            row: 1,
            order: 5,
            description:
                "Distance the offense must gain for a first down, used when a first down is earned by yards rather than by reaching the next zone."
        },
        // Internal marker-editor flag (set by the UI when the crew edits the
        // first-down spots) — never rendered as a form field.
        customFirstDownYardSet: {
            disabled: true
        },
        firstDownYardSet: {
            title: "First Down Spots",
            component: "fieldMarkers",
            markerKind: "point",
            visibleWhen: {
                key: "general.firstDownFlagStyle",
                values: [FirstDownStyles.ZoneToGain.code]
            },
            section: "fieldAreas",
            row: 1,
            order: 6,
            description:
                "The yardlines dividing the field into zones, used when a first down is earned by reaching the next zone."
        },
        fieldAreaSet: {
            // Named for the markers, not the area concept: the section is
            // already called Field Areas and a field repeating its section
            // name reads like a mistake.
            title: "Field Area Markers",
            component: "fieldMarkers",
            markerKind: "region",
            // NoRunZone hidden from the picker for now (no rule behavior yet);
            // existing/preset noRunZone areas still render in the editor.
            areaTypes: Object.values(FieldAreaType).filter(
                (t) => t.code !== FieldAreaType.NoRunZone.code
            ),
            section: "fieldAreas",
            row: 2,
            order: 1,
            description:
                "Regions of the field, such as red zones, marked out for stats and for display on the field graphic."
        },
        minutesByPeriod: {
            title: "Period Length (min)",
            order: 2,
            description: "Total time of a given period, such as a quarter."
        },
        // PSTATS-1268. Stored in General, drawn under Clock after Running
        // Clock (see football/config/custom-form-data.js).
        otTimeControl: {
            title: "OT Time Control",
            options: Object.values(BooleanType),
            section: "clock",
            order: 3,
            description: "Determines if time is tracked during overtime."
        },
        otMinutesByPeriod: {
            title: "OT Period Length (min)",
            min: 1,
            section: "clock",
            order: 4,
            visibleWhen: { key: "general.otTimeControl", values: [true] },
            description:
                "Total time of each overtime period while OT Time Control is on."
        },
        periodsInRegulation: {
            title: "Number of Periods",
            order: 1,
            description:
                "Total number of periods to end before the game is final or overtime proceeds."
        },
        redZoneLength: {
            disabled: true
        },
        zoneLength: {
            title: "Zone Length (yds)",
            order: 5,
            disabled: true
        },
        timeoutsPerHalf: {
            order: 8,
            description: "Total number of allotted timeouts per team each half."
        },
        // PSTATS-1268. The same order as Timeouts Per Half, whose key it
        // follows in the config, so the two draw together.
        otTimeouts: {
            title: "OT Timeouts",
            order: 8,
            description:
                "Total number of allotted timeouts per team in each overtime period, none carried over from regulation; 0 leaves timeouts unavailable in overtime."
        },
        rushType: {
            title: "Rush Type",
            options: Object.values(BooleanType),
            description:
                "Renders an additional step when entering a rush play to indicate the direction of the run."
        },
        passType: {
            title: "Pass Type",
            options: Object.values(BooleanType),
            description:
                "Renders an additional step when entering a pass play to indicate the direction of the pass."
        },
        // Legacy keys — clock settings live in the clock section now, but
        // configs saved before the move may still carry them in general.
        clockType: {
            disabled: true
        },
        runningClock: {
            disabled: true
        },
        spikeTeamAttOnly: {
            title: "Spike as Team Attempt Only",
            options: Object.values(BooleanType),
            description:
                "Determines whether a quarterback spike is counted as a pass attempt."
        },
        touchdownsCountAsFirstDowns: {
            title: "Touchdowns Count as 1st Downs",
            options: Object.values(BooleanType),
            description:
                "Given a touchdown, a first down is added automatically."
        },
        fumbledSnapEnabled: {
            title: "Fumbled Snap Play Entry",
            options: Object.values(BooleanType),
            description:
                "Determines if a fumbled snap can be recorded when entering a play."
        },
        sackFlowEnabled: {
            title: "Sack Play Entry",
            options: Object.values(BooleanType),
            description: "Determines if a sack can be recorded on a pass play."
        },
        fumbleFlowEnabled: {
            title: "Fumble Play Entry",
            options: Object.values(BooleanType),
            description: "Determines if a fumble can be recorded during a play."
        },
        defensivePatReturnEnabled: {
            title: "Defensive PAT Return",
            options: Object.values(BooleanType),
            description:
                "Determines if the defense can return an intercepted try for points."
        },
        kickoffEnabled: {
            title: "Kickoff Play Entry",
            options: Object.values(BooleanType),
            description:
                "Determines if kickoffs are recorded at the start of each half and after a score."
        },
        teamLossOnly: {
            title: "Team Loss Only",
            options: Object.values(TeamLoss),
            multiselect: true,
            description:
                "Determines if a quarterback loses rushing yards on a kneel down and/or sack, or only impacts team net total."
        },
        teamZoneQty: {
            disabled: true
        },
        participationStyle: {
            title: "Participation Style",
            options: Object.values(ParticipationStyle),
            description:
                "Determines whether participating players are set by the crew or collected automatically from the players named in each play."
        }
    },
    clock: {
        title: "Clock",
        // editableAfterStart: clock settings stay editable while the rest of
        // the form is locked mid-game (the save guard only allows clock-
        // section changes through).
        clockType: {
            title: "Clock Type",
            options: Object.values(ClockType),
            editableAfterStart: true,
            description:
                "Determines whether the clock time ascends or descends."
        },
        // No options: plain booleans render as a Switch in the form.
        runningClock: {
            title: "Running Clock",
            editableAfterStart: true,
            description:
                "BETA: Allows the game clock to run automatically, prefilling play time from the clock when entry begins."
        }
    },
    spots: {
        title: "Default Yardlines",
        order: 3,
        touchbackYardline: {
            title: "Touchback Yardline",
            description: "Spot where the ball is placed following a touchback."
        },
        // visibleWhen: see football/config/custom-form-data.js. OT Kickoffs
        // only acts while Kickoff Play Entry is on, so it follows that
        // switch, and Overtime Starting Yardline stays unless both are on: a
        // list, shown while either alternative holds.
        otKickoffs: {
            title: "OT Kickoffs",
            options: Object.values(BooleanType),
            visibleWhen: { key: "general.kickoffEnabled", values: [true] },
            description:
                "Determines if kickoffs follow scores in OT, when Kickoff Play Entry is on; while on, Overtime Starting Yardline is hidden."
        },
        overtimeStartingYardline: {
            title: "Overtime Starting Yardline",
            visibleWhen: [
                { key: "general.kickoffEnabled", values: [false] },
                { key: "spots.otKickoffs", values: [false] }
            ],
            description:
                "Spot where the ball is placed for new drives during overtime; hidden while OT Kickoffs is on."
        },
        kickoff: {
            title: "Kickoff Yardline",
            description:
                "Default yardline where the ball is placed given a kickoff."
        },
        // These two live under spots in the flag config. They were declared
        // under scoring here, where buildUiSchema never reads them, so both
        // fields fell back to the camelCase label and could carry no
        // definition (PSTATS-1267).
        driveStartingYardline: {
            title: "Drive Starting Yardline",
            description:
                "Spot where the ball is placed at the start of a drive."
        },
        safetyYardline: {
            // Spots draw as one flowing row, in the order below after the
            // yardlines above. A hard row break here left Overtime Starting
            // Yardline alone above the PAT line whenever OT Kickoffs showed
            // beside it (PSTATS-1268); flowing, the one extra field ends the
            // section instead, and the eight fields of every other state fill
            // two lines as before.
            order: 2,
            title: "Safety Yardline",
            description:
                "Spot where the ball is placed for the scoring team following a safety."
        },
        spotsPatScoreOption1: {
            title: "PAT Score (Option 1)",
            order: 3,
            description:
                "Yardline where the ball is snapped for the first point after attempt option."
        },
        spotsPatScoreOption2: {
            title: "PAT Score (Option 2)",
            order: 4,
            description:
                "Yardline where the ball is snapped for the second point after attempt option."
        },
        spotsPatScoreOption3: {
            title: "PAT Score (Option 3)",
            order: 5,
            description:
                "Yardline where the ball is snapped for the third point after attempt option."
        }
    },
    scoring: {
        title: "Scoring Information",
        order: 2,
        touchdown: {
            title: "Touchdown Score",
            row: 1,
            order: 1,
            description: "Number of points awarded for a touchdown."
        },
        safety: {
            title: "Safety Score",
            row: 1,
            order: 2,
            description: "Number of points awarded for a safety."
        },
        defensivePAT: {
            title: "Defensive PAT Score",
            row: 1,
            order: 3,
            description:
                "Number of points awarded for a try that is intercepted and returned to the end zone by the defense."
        },
        scoringPatScoreOption1: {
            title: PatPoints.OptionOne.name,
            row: 2,
            order: 1,
            description:
                "Number of points awarded for the first point after attempt option."
        },
        scoringPatScoreOption2: {
            title: PatPoints.OptionTwo.name,
            row: 2,
            order: 2,
            description:
                "Number of points awarded for the second point after attempt option."
        },
        scoringPatScoreOption3: {
            title: PatPoints.OptionThree.name,
            row: 2,
            order: 3,
            description:
                "Number of points awarded for the third point after attempt option."
        },
        playSummary: {
            disabled: true
        },
        rougePoints: {
            disabled: true
        },
        fieldGoal: {
            disabled: true
        }
    }
};

export default customFormData;
